// S-2211: Analysis error panel with actionable user messaging

import React from 'react';
import { AlertTriangle, RefreshCw, Camera, Zap, CreditCard, Clock, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AnalysisError, AnalysisErrorType } from '@/lib/analysis/error-types';

interface ErrorPanelProps {
  error: AnalysisError;
  onRetry?: () => void;
  onRecordNew?: () => void;
  onUpgrade?: () => void;
  onShowTips?: () => void;
  className?: string;
}

const ERROR_ICONS = {
  [AnalysisErrorType.NETWORK_ERROR]: RefreshCw,
  [AnalysisErrorType.AUTH_ERROR]: AlertTriangle,
  [AnalysisErrorType.VIDEO_NOT_READY]: Clock,
  [AnalysisErrorType.INVALID_REQUEST]: AlertTriangle,
  [AnalysisErrorType.TRANSIENT_ERROR]: RefreshCw,
  [AnalysisErrorType.UNKNOWN_ERROR]: AlertTriangle,
  [AnalysisErrorType.CLIENT_PAYLOAD_INVALID]: Camera,
  [AnalysisErrorType.NETWORK_OR_CORS_FAILURE]: Globe,
  // S-2243: New error types
  [AnalysisErrorType.ANALYSIS_INPUT_MISSING]: Zap,
  [AnalysisErrorType.MODEL_REJECTED]: AlertTriangle,
  [AnalysisErrorType.RETRYABLE_UPSTREAM]: RefreshCw,
};

const ERROR_COLORS = {
  [AnalysisErrorType.NETWORK_ERROR]: 'border-yellow-200 bg-yellow-50',
  [AnalysisErrorType.AUTH_ERROR]: 'border-purple-200 bg-purple-50',
  [AnalysisErrorType.VIDEO_NOT_READY]: 'border-gray-200 bg-gray-50',
  [AnalysisErrorType.INVALID_REQUEST]: 'border-orange-200 bg-orange-50',
  [AnalysisErrorType.TRANSIENT_ERROR]: 'border-blue-200 bg-blue-50',
  [AnalysisErrorType.UNKNOWN_ERROR]: 'border-red-200 bg-red-50',
  [AnalysisErrorType.CLIENT_PAYLOAD_INVALID]: 'border-orange-200 bg-orange-50',
  [AnalysisErrorType.NETWORK_OR_CORS_FAILURE]: 'border-yellow-200 bg-yellow-50',
  // S-2243: New error types
  [AnalysisErrorType.ANALYSIS_INPUT_MISSING]: 'border-yellow-200 bg-yellow-50',
  [AnalysisErrorType.MODEL_REJECTED]: 'border-red-200 bg-red-50',
  [AnalysisErrorType.RETRYABLE_UPSTREAM]: 'border-blue-200 bg-blue-50',
};

export function ErrorPanel({ 
  error, 
  onRetry, 
  onRecordNew, 
  onUpgrade, 
  onShowTips,
  className = '' 
}: ErrorPanelProps) {
  const Icon = ERROR_ICONS[error.type];
  const colorClass = ERROR_COLORS[error.type];

  const handleAction = () => {
    switch (error.type) {
      case AnalysisErrorType.CLIENT_PAYLOAD_INVALID:
        onRecordNew?.();
        break;
      case AnalysisErrorType.ANALYSIS_INPUT_MISSING:
        onShowTips?.();
        break;
      case AnalysisErrorType.AUTH_ERROR:
        // No action - user needs to re-login
        break;
      case AnalysisErrorType.INVALID_REQUEST:
        // No action - video not available
        break;
      case AnalysisErrorType.MODEL_REJECTED:
        // No retry - model rejection
        break;
      case AnalysisErrorType.RETRYABLE_UPSTREAM:
      case AnalysisErrorType.TRANSIENT_ERROR:
      case AnalysisErrorType.NETWORK_ERROR:
      case AnalysisErrorType.NETWORK_OR_CORS_FAILURE:
      case AnalysisErrorType.VIDEO_NOT_READY:
      case AnalysisErrorType.UNKNOWN_ERROR:
      default:
        onRetry?.();
        break;
    }
  };

  const getContextDetails = () => {
    const { context } = error;
    
    switch (error.type) {
      case AnalysisErrorType.CLIENT_PAYLOAD_INVALID:
        return (
          <div className="mt-3 text-sm text-gray-600">
            <p><strong>Invalid field:</strong> {context.field}</p>
            {context.remediation && (
              <p className="mt-2 text-xs">
                💡 <strong>How to fix:</strong> {context.remediation}
              </p>
            )}
          </div>
        );
      
      case AnalysisErrorType.ANALYSIS_INPUT_MISSING:
        return (
          <div className="mt-3 text-sm text-gray-600">
            <p><strong>Missing data:</strong> {context.field}</p>
            <div className="mt-2 text-xs space-y-1">
              <p>💡 <strong>Recording tips:</strong></p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>Ensure good lighting (avoid backlighting)</li>
                <li>Keep your full body visible in frame</li>
                <li>Use a stable camera position</li>
                <li>Wear contrasting colors against background</li>
              </ul>
            </div>
          </div>
        );
      
      case AnalysisErrorType.MODEL_REJECTED:
        return context.reason ? (
          <div className="mt-3 text-sm text-gray-600">
            <p><strong>Reason:</strong> {context.reason}</p>
          </div>
        ) : null;
      
      case AnalysisErrorType.NETWORK_OR_CORS_FAILURE:
        return context.chunkNumber ? (
          <div className="mt-3 text-sm text-gray-600">
            <p><strong>Failed at chunk:</strong> {context.chunkNumber}</p>
          </div>
        ) : null;
      
      default:
        return null;
    }
  };

  return (
    <Alert className={`${colorClass} ${className}`}>
      <Icon className="h-5 w-5" />
      <AlertDescription className="ml-2">
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">
              Analysis Issue Detected
            </h4>
            <p className="text-gray-700">{error.userMessage}</p>
          </div>

          {getContextDetails()}

          {error.context.suggestedAction && (
            <div className="bg-white/50 rounded-md p-3 border border-gray-200">
              <p className="text-sm font-medium text-gray-800">
                📋 Suggested Action
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {error.context.suggestedAction}
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {/* Retry button for retryable errors */}
            {error.retryable && (
              <Button 
                onClick={onRetry}
                variant="default"
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
            )}
            
            {/* Record new video for client payload errors */}
            {error.type === AnalysisErrorType.CLIENT_PAYLOAD_INVALID && onRecordNew && (
              <Button 
                onClick={onRecordNew}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Camera className="h-4 w-4" />
                Record New Video
              </Button>
            )}
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}

// Hook for managing error panel state
export function useAnalysisErrorPanel() {
  const [error, setError] = React.useState<AnalysisError | null>(null);

  const showError = (analysisError: AnalysisError) => {
    console.error('[S-2211] Analysis error:', {
      type: analysisError.type,
      message: analysisError.message,
      context: analysisError.context
    });
    setError(analysisError);
  };

  const clearError = () => {
    setError(null);
  };

  return {
    error,
    showError,
    clearError,
    hasError: error !== null
  };
}
